import { useState } from "react";
import { Plus, Minus } from "lucide-react";

const INK = "#141311";
const BRONZE = "#A89880";
const MUTED = "#8A8071";

export const FAQS = [
  {
    q: "Who is the Metabolic Transformation Method for?",
    a: "MTM is for women who feel like their body stopped responding — you're eating less, moving more, and nothing changes. Whether you're in your 30s, 40s, or navigating perimenopause, if you're dealing with fatigue, stubborn weight, bloating, or hormone chaos, this method was built for you. You don't need to be 'fit' to start. You just need to be ready to stop guessing.",
  },
  {
    q: "How is this different from every other program I've tried?",
    a: "Most programs hand you a meal plan and more cardio. MTM heals the reason your body stopped responding in the first place. We follow a specific four-phase order — Detox, Restore, Burn, Reset — so your body is actually ready for fat loss before we ever ask it to lose fat. That's why the results last.",
  },
  {
    q: "How much time do I need each week?",
    a: "About 3–4 hours of training per week, daily walks, and a few minutes a day for check-ins and rituals. The program is built for real life — stay-at-home moms, shift workers, and corporate women all move through it successfully. Group calls are recorded if you can't make them live.",
  },
  {
    q: "Do I need a gym membership?",
    a: "No. Your training can be built for home (with minimal equipment like dumbbells and bands) or for the gym — whichever fits your life. Every exercise comes with a video demonstration so you're never guessing on form.",
  },
  {
    q: "I have thyroid issues, PCOS, or I'm in perimenopause — will this still work?",
    a: "These are exactly the women I work with most. MTM is a root-cause, hormone-aware method — we work with your physiology, not against it. That said, this is coaching, not medical care: I'll always encourage you to keep your doctor in the loop, and the method complements (never replaces) medical treatment.",
  },
  {
    q: "What exactly is included in the 6 months?",
    a: "The full four-phase MTM curriculum with weekly content drops, group coaching calls, your training programs with video demos, functional nutrition and detox protocols, habit and progress tracking, community support, and guidance on supplements. Everything is delivered inside your private member portal.",
  },
  {
    q: "Are there payment plans?",
    a: "Yes. We go over the investment and flexible payment options together on your call — including plans that fit your situation. No surprises, no pressure.",
  },
  {
    q: "What's the difference between MTM and Rise Within 1:1?",
    a: "MTM is the group experience — the complete method, moving through the phases alongside a community of women. Rise Within is my elite 1:1 container: functional lab testing, fully bespoke protocols, weekly private check-ins, and direct access to me. I only hold 10 1:1 spots at a time, by application.",
  },
  {
    q: "What happens when the 24 weeks are over?",
    a: "Phase 4 — Reset — is the whole point. We reverse diet you out of a deficit, lock in your results, and hand you the tools to maintain them without extremes. You leave with a healed metabolism and the knowledge to keep it, not a rebound waiting to happen.",
  },
  {
    q: "What if I'm not sure which path is right for me?",
    a: "Book a free 20-minute discovery call. No pressure, no pitch marathon — just clarity on where your body is right now and which door makes sense for you.",
  },
];

export function FAQSection() {
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className="px-6 md:px-12 py-16 md:py-28 max-w-[900px] mx-auto" data-testid="faq-section">
      <p className="lp-label-sm" style={{ color: BRONZE }}>Questions ·</p>
      <h2 className="lp-h mt-5 text-4xl md:text-5xl">
        Asked <span className="lp-it normal-case" style={{ color: BRONZE }}>&amp; answered.</span>
      </h2>
      <div className="mt-12">
        {FAQS.map((f, i) => {
          const isOpen = open === i;
          return (
            <div key={i} className="border-t" style={{ borderColor: "rgba(20,19,17,0.12)" }} data-testid={`faq-item-${i + 1}`}>
              <button
                className="w-full flex items-center justify-between gap-6 py-6 text-left"
                onClick={() => setOpen(isOpen ? -1 : i)}
                aria-expanded={isOpen}
                data-testid={`faq-question-${i + 1}`}
              >
                <span className="lp-h text-xl md:text-2xl normal-case" style={{ textTransform: "none", color: INK }}>{f.q}</span>
                <span className="flex-shrink-0" style={{ color: BRONZE }}>
                  {isOpen ? <Minus size={18} /> : <Plus size={18} />}
                </span>
              </button>
              <div
                className="overflow-hidden transition-all duration-300"
                style={{ maxHeight: isOpen ? 1000 : 0, opacity: isOpen ? 1 : 0 }}
                data-testid={`faq-answer-${i + 1}`}
              >
                <p className="pb-7 text-base md:text-lg leading-relaxed max-w-2xl" style={{ color: MUTED }}>{f.a}</p>
              </div>
            </div>
          );
        })}
        <div className="border-t" style={{ borderColor: "rgba(20,19,17,0.12)" }} />
      </div>
    </section>
  );
}
